import { useMemo } from "react";

import type { ThreadId } from "@taskin-code/contracts";
import { resolveThreadWorkspaceCwd } from "@taskin-code/shared/threadEnvironment";

import { useStore } from "~/store";
import { createProjectSelector, createThreadSelector } from "~/storeSelectors";

/**
 * Resolve the workspace cwd for a thread (worktree path when in worktree mode,
 * otherwise the project root). Returns null when the thread or its project
 * cannot be resolved.
 */
export function useThreadWorkspaceCwd(threadId: ThreadId | null | undefined): string | null {
  const selectThread = useMemo(() => createThreadSelector(threadId), [threadId]);
  const thread = useStore(selectThread);

  const selectProject = useMemo(
    () => createProjectSelector(thread?.projectId ?? null),
    [thread?.projectId],
  );
  const project = useStore(selectProject);

  return useMemo(
    () =>
      resolveThreadWorkspaceCwd({
        projectCwd: project?.cwd ?? null,
        envMode: thread?.envMode ?? null,
        worktreePath: thread?.worktreePath ?? null,
      }),
    [project?.cwd, thread?.envMode, thread?.worktreePath],
  );
}
